import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { withDateRange } from '@/helpers/dateRangeFilename';

type DownloadCreditUsageParams = {
  organisationId: string;
  startDate: Date;
  endDate: Date;
};

function toUtcCalendarDate(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

export const useDownloadCreditUsage = () => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadCreditUsage = async ({ organisationId, startDate, endDate }: DownloadCreditUsageParams) => {
    const normalizedStartDate = toUtcCalendarDate(startDate);
    const normalizedEndDate = toUtcCalendarDate(endDate);
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: { organisationId, startDate: normalizedStartDate, endDate: normalizedEndDate },
    });

    const response = await fetch(`${APIBaseChronos}/api/usage/ledger-billing-overview/download`, fetchConfig);
    if (!response.ok) throw new Error('Failed to download credit usage');

    const blob = await response.blob();
    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: withDateRange('credit-usage', 'xlsx', normalizedStartDate, normalizedEndDate),
    });
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const { mutate: fetchDownload, isLoading } = useMutation(downloadCreditUsage, {
    onSuccess: () => {
      trackCustomEvent(TrackingEventsV2.UsageDashboardDownloadCreditUsage);
    },
    onError: () => {
      toast.error('Failed to download credit usage. Please try again.');
    },
  });

  return { fetchDownload, isLoading };
};
